
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  Button,
  Avatar,
  Divider,
  Stack,
  CircularProgress,
  TextField,
  Checkbox,
  IconButton,
  Paper,
  LinearProgress,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import BusinessIcon from "@mui/icons-material/Business";
import HealthIcon from "@mui/icons-material/LocalHospital";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloudIcon from "@mui/icons-material/Cloud";
import SchoolIcon from "@mui/icons-material/School";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

// ── Styled components (unchanged) ──────────────────────────────────────────
const GradientButton = styled(Button)({
  background: "linear-gradient(180deg, #6BD0F6, #00AEEF)",
  color: "#FFFFFF",
  fontWeight: "bold",
  fontSize: 12,
  textTransform: "none",
  borderRadius: 6,
  padding: "8px 24px",
  "&:hover": { background: "linear-gradient(180deg, #5BC0E6, #009ADF)" },
});

// ── Filter constants (unchanged) ────────────────────────────────────────────
const EXPERTISE_OPTIONS = [
  "Business Strategy",
  "Digital Marketing",
  "Product Development",
  "Fundraising & Investment",
  "UX/UI Design",
];

const INDUSTRY_OPTIONS = [
  { label: "FinTech",     icon: <BusinessIcon    fontSize="small" /> },
  { label: "HealthTech",  icon: <HealthIcon      fontSize="small" /> },
  { label: "E-commerce",  icon: <ShoppingCartIcon fontSize="small" /> },
  { label: "SaaS",        icon: <CloudIcon        fontSize="small" /> },
  { label: "EdTech",      icon: <SchoolIcon       fontSize="small" /> },
];

const PROJECTS_PER_PAGE = 3;

export default function CurrentProjects() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const [projects,        setProjects]        = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error,           setError]           = useState("");
  const [searchTerm,      setSearchTerm]      = useState("");
  const [selectedExpertise, setSelectedExpertise] = useState([]);
  const [selectedIndustry,  setSelectedIndustry]  = useState([]);
  const [currentPage,     setCurrentPage]     = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const user   = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id || user?._id;

  // ── Fetch projects ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingProjects(true);
        setError("");
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/projects");
        const userProjects = Array.isArray(res.data)
          ? res.data.filter((p) => String(p.userId) === String(userId))
          : [];
        setProjects(userProjects);
        setCurrentPage(1);
      } catch (err) {
        setError("Failed to load projects.");
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, [userId]);

  // ── Filter helpers ─────────────────────────────────────────────────────
  const toggleExpertise = (item) => {
    setSelectedExpertise((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
    setCurrentPage(1);
  };

  const toggleIndustry = (label) => {
    setSelectedIndustry((prev) =>
      prev.includes(label) ? prev.filter((i) => i !== label) : [...prev, label]
    );
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSelectedExpertise([]);
    setSelectedIndustry([]);
    setSearchTerm("");
    setCurrentPage(1);
    if (isMobile) setFilterDrawerOpen(false);
  };

  // ── Derived filtered list ──────────────────────────────────────────────
  const filteredProjects = useMemo(() => {
    let result = [...projects];
    if (searchTerm?.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter(
        (p) =>
          (p.title       && p.title.toLowerCase().includes(q))       ||
          (p.category    && p.category.toLowerCase().includes(q))    ||
          (p.description && p.description.toLowerCase().includes(q))
      );
    }
    if (selectedExpertise.length > 0) {
      result = result.filter((p) => {
        if (Array.isArray(p.areasOfExpertise) && p.areasOfExpertise.length) {
          const lowerSet = p.areasOfExpertise.map((s) => String(s).toLowerCase());
          return selectedExpertise.some((sel) => lowerSet.includes(sel.toLowerCase()));
        }
        const hay = `${p.category || ""} ${p.description || ""}`.toLowerCase();
        return selectedExpertise.some((sel) => hay.includes(sel.toLowerCase()));
      });
    }
    if (selectedIndustry.length > 0) {
      result = result.filter((p) => {
        const industryField = (p.industry || p.category || "").toString().toLowerCase();
        return selectedIndustry.some((sel) => industryField.includes(sel.toLowerCase()));
      });
    }
    return result;
  }, [projects, searchTerm, selectedExpertise, selectedIndustry]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE));

  const paginatedProjects = useMemo(() => {
    const start = (currentPage - 1) * PROJECTS_PER_PAGE;
    return filteredProjects.slice(start, start + PROJECTS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const trimDescription = (text, maxWords = 20) => {
    if (!text) return "";
    const words = String(text).split(/\s+/);
    return words.length <= maxWords ? text : words.slice(0, maxWords).join(" ") + "...";
  };

  // Active filter count for badge on mobile button
  const activeFilterCount = selectedExpertise.length + selectedIndustry.length + (searchTerm.trim() ? 1 : 0);

  // ── Filter panel — shared between sidebar and drawer ──────────────────
  const FilterPanel = () => (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#979797", fontWeight: "bold" }}>
          Browse Filters
        </Typography>
        {isMobile && (
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "#979797" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ width: "100%", mb: 2 }} />

      {/* Search */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">Expertise</Typography>
        <IconButton size="small" color="primary"><SearchIcon /></IconButton>
      </Stack>
      <TextField
        placeholder="Search projects..."
        size="small"
        variant="outlined"
        fullWidth
        value={searchTerm}
        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        InputProps={{ startAdornment: <SearchIcon sx={{ color: "#979797", mr: 1 }} /> }}
        sx={{ mb: 2 }}
      />

      {/* Expertise checkboxes */}
      {EXPERTISE_OPTIONS.map((item, index) => (
        <Stack key={index} direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mb: 1 }}>
          <Typography sx={{ color: "#6C6C6C", fontSize: { xs: 13, sm: 14 } }}>{item}</Typography>
          <Checkbox size="small" checked={selectedExpertise.includes(item)} onChange={() => toggleExpertise(item)} />
        </Stack>
      ))}

      <Divider sx={{ width: "100%", my: 2 }} />

      {/* Industry checkboxes */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">Industry</Typography>
        <IconButton size="small"><FilterListIcon /></IconButton>
      </Stack>
      {INDUSTRY_OPTIONS.map((item, index) => (
        <Stack key={index} direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%", mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {item.icon}
            <Typography sx={{ color: "#6C6C6C", fontSize: { xs: 13, sm: 14 } }}>{item.label}</Typography>
          </Stack>
          <Checkbox size="small" checked={selectedIndustry.includes(item.label)} onChange={() => toggleIndustry(item.label)} />
        </Stack>
      ))}

      <Divider sx={{ width: "100%", my: 2 }} />

      <Button
        variant="outlined"
        size="small"
        onClick={resetFilters}
        sx={{ textTransform: "none", width: "100%" }}
      >
        Reset Filters
      </Button>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", background: "#FFFFFF", minHeight: "100vh", overflowX: "hidden", width: "100%", boxSizing: "border-box" }}>
      <Box sx={{ flex: 1, background: "#F6F7F9" }}>
        <TopBar />

        {/* ── Page header ──────────────────────────────────────────────── */}
        <Box sx={{
          px: { xs: "12px", sm: "24px", md: "38px" },
          pt: { xs: 2, sm: 3 },
          mb: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, sm: 0 },
        }}>
          <Typography variant="h3" sx={{ color: "#010101", fontWeight: "bold", fontSize: { xs: 24, sm: 32, md: 40 } }}>
            Projects
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/createProjects")}
            sx={{
              bgcolor: "#E6F7FD", color: "#00AEEF", fontWeight: "bold",
              textTransform: "none", borderRadius: 1,
              fontSize: { xs: 12, sm: 14 },
              "&:hover": { bgcolor: "#d4f1fa" },
            }}
          >
            + New Project
          </Button>
        </Box>

        {/* ── Main layout ───────────────────────────────────────────────── */}
        <Box sx={{
          px: { xs: "12px", sm: "24px", md: "38px" },
          pb: { xs: 4, sm: 6 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 3 },
          alignItems: "flex-start",
          width: "100%",
          boxSizing: "border-box",
        }}>

          {/* ── Desktop filter sidebar ────────────────────────────────── */}
          {!isMobile && (
            <Box sx={{
              flexShrink: 0,
              width: { md: 280, lg: 320 },
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
              p: 3,
              position: "sticky",
              top: 16,
              boxSizing: "border-box",
            }}>
              <FilterPanel />
            </Box>
          )}

          {/* ── Right panel: projects list ────────────────────────────── */}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%", boxSizing: "border-box" }}>

            {/* List header */}
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              mb: 1.5, flexWrap: "wrap", gap: 1,
            }}>
              <Typography sx={{ color: "#979797", fontSize: { xs: 14, sm: 16 }, fontWeight: "bold" }}>
                Current Projects
              </Typography>
              {/* Filter button — mobile only */}
              {isMobile && (
                <Button
                  size="small"
                  startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    bgcolor: "#E6F7FD", color: "#00AEEF",
                    textTransform: "none", fontWeight: "bold",
                    fontSize: 12, borderRadius: 1, px: 1.5, py: 0.5,
                  }}
                >
                  Filters
                  {activeFilterCount > 0 && (
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#F57E47", ml: 0.5 }} />
                  )}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2, borderColor: "#E6E6E6" }} />

            {/* ── Loading / Error / Empty ───────────────────────────── */}
            {loadingProjects ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 6 }}>
                <CircularProgress size={28} sx={{ color: "#00AEEF" }} />
              </Box>
            ) : error ? (
              <Typography color="error" sx={{ fontSize: 13 }}>{error}</Typography>
            ) : filteredProjects.length === 0 ? (
              <Typography sx={{ color: "#6C6C6C", fontSize: 14 }}>No projects found.</Typography>
            ) : (
              <>
                {/* ── Project cards ─────────────────────────────────── */}
                <Card elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: 3, bgcolor: "white", width: "100%", boxSizing: "border-box" }}>
                  <Divider sx={{ mb: 2 }} />

                  {paginatedProjects.map((project) => (
                    <Paper
                      key={project._id}
                      elevation={1}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        transition: "0.2s",
                        width: "100%",
                        boxSizing: "border-box",
                        "&:hover": { backgroundColor: "#f1f5fb" },
                      }}
                    >
                      {/* Row header: icon + title */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, mb: 1, flexWrap: "nowrap", overflow: "hidden" }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: { xs: 40, sm: 55 }, height: { xs: 40, sm: 55 },
                            bgcolor: "#E8F4FF", color: "#007BCE", flexShrink: 0,
                          }}
                        >
                          <InsertDriveFileIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: 13, sm: 16 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {project.title}
                        </Typography>
                      </Box>

                      {/* Details */}
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: 12, sm: 14 } }}>
                        <strong>Category:</strong> {project.category}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        <strong>Status:</strong> {project.status}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        <strong>Completion:</strong> {project.completion}%
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={project.completion}
                        sx={{ my: 1, height: 6, borderRadius: 3 }}
                      />

                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: 11, sm: 13 }, color: "#6C6C6C" }}>
                        {trimDescription(project.description, 20)}
                      </Typography>

                      {/* Actions */}
                      <Box display="flex" justifyContent="flex-end" mt={1.5}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`/projects/${project._id}`)}
                          sx={{
                            bgcolor: "#1976d2",
                            textTransform: "none",
                            fontSize: { xs: 11, sm: 13 },
                            px: { xs: 1.5, sm: 2 },
                            width: { xs: "100%", sm: "auto" },
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Card>

                {/* ── Pagination ────────────────────────────────────── */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={3}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Button
                    variant="outlined"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    sx={{ textTransform: "none", fontSize: { xs: 12, sm: 14 }, borderColor: "#00AEEF", color: "#00AEEF", "&:disabled": { borderColor: "#E6E6E6", color: "#979797" } }}
                  >
                    Prev
                  </Button>
                  <Typography sx={{ color: "#6C6C6C", fontSize: { xs: 12, sm: 14 } }}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    sx={{ textTransform: "none", fontSize: { xs: 12, sm: 14 }, borderColor: "#00AEEF", color: "#00AEEF", "&:disabled": { borderColor: "#E6E6E6", color: "#979797" } }}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Footer />
      </Box>

      {/* ── Mobile: filter bottom drawer ─────────────────────────────── */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius:  20,
              borderTopRightRadius: 20,
              maxHeight: "85vh",
              overflowY: "auto",
              px: "16px",
              pt: "16px",
              pb: "32px",
            },
          }}
        >
          {/* Drag handle */}
          <Box sx={{ width: 40, height: 4, bgcolor: "#E6E6E6", borderRadius: 2, mx: "auto", mb: 2 }} />
          <FilterPanel />
        </Drawer>
      )}
    </Box>
  );
}
